import { useEffect, useRef, useState } from "react";

function isMediaFile(file) {
  const type = String(file?.type || "").toLowerCase();
  return type.startsWith("image/") || type.startsWith("video/");
}

function isVideoFile(file) {
  return String(file?.type || "").startsWith("video/");
}

function mediaPath(media) {
  return typeof media === "string" ? media : String(media?.key || "");
}

function isVideoPath(path) {
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(String(path || ""));
}

function mediaSrc(path) {
  const raw = String(path || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw}`;
}

export function pendingMediaKey(file) {
  return `pending:${file.name}:${file.lastModified}:${file.size}`;
}

function IconUpload() {
  return (
    <svg className="mx-auto h-8 w-8 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M12 16V7m0 0 3.5 3.5M12 7 8.5 10.5" />
      <path d="M5 16.5V18a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1.5" />
    </svg>
  );
}

function IconClose({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function RoleButtons({
  disabled,
  isPrimary,
  isSecondary,
  onPrimary,
  onSecondary,
  primaryLabel,
  secondaryLabel,
  primaryOnly = false,
}) {
  return (
    <div className="absolute bottom-0 left-0 right-0 flex gap-0.5 p-0.5">
      <button
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onPrimary();
        }}
        className={`flex-1 rounded px-0.5 py-0.5 text-[10px] font-semibold leading-tight ${
          isPrimary ? "bg-[#c9a46a] text-white" : "bg-white/90 text-slate-700 hover:bg-[#f8f1e4]"
        } disabled:opacity-50`}
        aria-pressed={isPrimary}
      >
        {primaryLabel}
      </button>
      {primaryOnly ? null : (
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onSecondary();
          }}
          className={`flex-1 rounded px-0.5 py-0.5 text-[10px] font-semibold leading-tight ${
            isSecondary ? "bg-amber-500 text-white" : "bg-white/90 text-slate-700 hover:bg-amber-50"
          } disabled:opacity-50`}
          aria-pressed={isSecondary}
        >
          {secondaryLabel}
        </button>
      )}
    </div>
  );
}

function PortalMediaUpload({
  label,
  hint,
  dropText,
  roleHint,
  primaryLabel = "Prim",
  secondaryLabel = "Sec",
  existingMedia = [],
  pendingFiles = [],
  onPendingFilesChange,
  onRemoveExisting,
  disabled = false,
  primaryImage = "",
  secondaryImage = "",
  onPrimaryImageChange,
  onSecondaryImageChange,
  primaryOnly = false,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const rolesEnabled = typeof onPrimaryImageChange === "function";

  useEffect(() => {
    const urls = pendingFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [pendingFiles]);

  function addFiles(fileList) {
    if (disabled || !fileList?.length) return;
    const next = Array.from(fileList).filter(isMediaFile);
    if (!next.length) return;
    onPendingFilesChange([...pendingFiles, ...next]);
  }

  function removePending(index) {
    const removed = pendingFiles[index];
    const key = removed ? pendingMediaKey(removed) : "";
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
    if (rolesEnabled && key) {
      if (primaryImage === key) onPrimaryImageChange("");
      if (!primaryOnly && secondaryImage === key) onSecondaryImageChange?.("");
    }
  }

  function selectPrimary(key) {
    if (!rolesEnabled || disabled) return;
    if (primaryImage === key) {
      onPrimaryImageChange("");
      return;
    }
    onPrimaryImageChange(key);
    if (!primaryOnly && secondaryImage === key) onSecondaryImageChange?.("");
  }

  function selectSecondary(key) {
    if (!rolesEnabled || disabled || primaryOnly) return;
    if (secondaryImage === key) {
      onSecondaryImageChange?.("");
      return;
    }
    onSecondaryImageChange?.(key);
    if (primaryImage === key) onPrimaryImageChange("");
  }

  return (
    <div className="flex h-full flex-col">
      <p className="text-sm font-medium text-slate-700">{label}</p>
      {rolesEnabled && roleHint ? (
        <p className="mt-0.5 text-xs text-slate-500">{roleHint}</p>
      ) : null}
      <div
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          addFiles(event.dataTransfer.files);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`mt-1 flex h-40 min-h-[10rem] flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
          dragOver ? "border-[#c9a46a] bg-[#f8f1e4]" : "border-slate-300 bg-slate-50"
        } ${disabled ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <IconUpload />
        <p className="mt-1 text-sm text-slate-600">{dropText}</p>
        <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
      </div>

      {existingMedia.length > 0 || pendingFiles.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {existingMedia.map((media, index) => {
            const path = mediaPath(media);
            const src = mediaSrc(path);
            const isVideo = isVideoPath(path);
            const isPrimary = primaryImage === path;
            const isSecondary = secondaryImage === path;
            const ring = isPrimary
              ? "ring-2 ring-[#c9a46a]"
              : isSecondary
                ? "ring-2 ring-amber-500"
                : "";
            return (
              <div key={`${path || "saved"}-${index}`} className="group relative">
                {isVideo ? (
                  <video
                    src={src}
                    className={`h-24 w-24 cursor-pointer rounded border border-slate-200 object-cover ${ring}`}
                    muted
                    preload="metadata"
                    onClick={(event) => {
                      event.stopPropagation();
                      setLightbox({ src, kind: "video" });
                    }}
                  />
                ) : (
                  <img
                    src={src}
                    alt=""
                    className={`h-24 w-24 cursor-pointer rounded border border-slate-200 object-cover hover:ring-2 hover:ring-[#c9a46a] ${ring}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setLightbox({ src, kind: "photo" });
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveExisting(index);
                  }}
                  className="absolute -right-1 -top-1 z-10 rounded-full bg-rose-500 p-0.5 text-white"
                  aria-label="Remove saved media"
                >
                  <IconClose className="h-4 w-4" />
                </button>
                {rolesEnabled && !isVideo ? (
                  <RoleButtons
                    disabled={disabled}
                    isPrimary={isPrimary}
                    isSecondary={isSecondary}
                    onPrimary={() => selectPrimary(path)}
                    onSecondary={() => selectSecondary(path)}
                    primaryLabel={primaryLabel}
                    secondaryLabel={secondaryLabel}
                    primaryOnly={primaryOnly}
                  />
                ) : null}
              </div>
            );
          })}

          {pendingFiles.map((file, index) => {
            const src = previewUrls[index];
            const isVideo = isVideoFile(file);
            const key = pendingMediaKey(file);
            const isPrimary = primaryImage === key;
            const isSecondary = secondaryImage === key;
            const ring = isPrimary
              ? "ring-2 ring-[#c9a46a]"
              : isSecondary
                ? "ring-2 ring-amber-500"
                : "";
            return (
              <div key={key} className="group relative">
                {isVideo ? (
                  <video
                    src={src}
                    className={`h-24 w-24 cursor-pointer rounded border border-[#c9a46a] object-cover ${ring}`}
                    muted
                    preload="metadata"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (src) setLightbox({ src, kind: "video" });
                    }}
                  />
                ) : (
                  <img
                    src={src}
                    alt={file.name}
                    className={`h-24 w-24 cursor-pointer rounded border border-[#c9a46a] object-cover hover:ring-2 hover:ring-[#c9a46a] ${ring}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (src) setLightbox({ src, kind: "photo" });
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    removePending(index);
                  }}
                  className="absolute -right-1 -top-1 z-10 rounded-full bg-rose-500 p-0.5 text-white"
                  aria-label={`Remove ${file.name}`}
                >
                  <IconClose className="h-4 w-4" />
                </button>
                {rolesEnabled && !isVideo ? (
                  <RoleButtons
                    disabled={disabled}
                    isPrimary={isPrimary}
                    isSecondary={isSecondary}
                    onPrimary={() => selectPrimary(key)}
                    onSecondary={() => selectSecondary(key)}
                    primaryLabel={primaryLabel}
                    secondaryLabel={secondaryLabel}
                    primaryOnly={primaryOnly}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {lightbox ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
        >
          {lightbox.kind === "video" ? (
            <video
              src={lightbox.src}
              className="max-h-[90vh] max-w-full rounded shadow-lg"
              controls
              autoPlay
              onClick={(event) => event.stopPropagation()}
            />
          ) : (
            <img
              src={lightbox.src}
              alt=""
              className="max-h-[90vh] max-w-full rounded object-contain shadow-lg"
              onClick={(event) => event.stopPropagation()}
              draggable={false}
            />
          )}
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-800 hover:bg-white"
            aria-label="Close"
          >
            <IconClose className="h-6 w-6" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default PortalMediaUpload;
